"use client";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn, dayjs } from "utils";
import log from "logbeacon";
import { useMemoizedFn } from "ahooks";
import { TrackerReportItemLoading } from "./TrackerReportItemLoading";
import { I18nUtil } from "@utils/i18n";
import {
  useIsPc,
  useMemberUser,
  useOpenChatModal,
  useSummaryStream,
} from "@/src/hooks";
import { Markdown } from "@/src/components/Markdown";
import { LOCAL_STORAGE_KEYS, QUERY_KEYS } from "@/src/config/constant";
import Translations from "@translate/components/CoPilot/CopilotAgent/TimeLineItem/TrackerReportItem";
import { useScrollManager } from "@/src/hooks/useScrollManager";
import EdgenDrawerCallable from "@/src/components/EdgenDrawerCallable";
import { EdgenModalCreateAble } from "@components/EdgenModal";

const I18n = I18nUtil.createScoped(Translations);

interface TrackerReportItemProps {
  type?: "list" | "modal";
  id?: string;
  isExpanded?: boolean;
  toggleExpand?: (value: boolean) => void;
  summary?: string;
  created?: number;
  reportType?: "DAILY_REPORT" | "SUMMARY_NEWS";
  onChartClick?: (value: {
    question?: string;
    title?: string;
    desc?: string;
    id?: string;
    link?: string;
    type?: string;
  }) => void;
  modalParams?: {
    onClose?: () => void;
    isStatusLoading?: boolean;
  };
}

const LoadingSkeleton: React.FC = () => (
  <div className="flex flex-col">
    {/* 第一组骨架屏 */}
    <div className="flex flex-col gap-[10px]">
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[319px] md:w-[702px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[92px] md:w-[202px]" />
    </div>
    <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[24px] w-[255px] mt-10 md:w-[555px]" />

    {/* 第二组骨架屏 */}
    <div className="flex flex-col gap-[10px] mt-4">
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[204px] md:w-[404px]" />
    </div>
    <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[24px] w-[255px] mt-10 md:w-[555px]" />

    {/* 第三组骨架屏 */}
    <div className="flex flex-col gap-[10px] mt-4">
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[361px] md:w-[752px]" />
      <div className="bg-background_white_overlay5 animate-pulse rounded-[4px] h-[18px] w-[204px] md:w-[404px]" />
    </div>
  </div>
);

interface H5LoadingProps {
  title?: string;
  onClose?: () => void;
  reportType?: "DAILY_REPORT" | "SUMMARY_NEWS";
}

const H5Loading: React.FC<H5LoadingProps> = ({
  title,
  onClose,
  reportType = "DAILY_REPORT",
}) => (
  <div className="w-full flex flex-row items-center justify-between">
    <div className="flex flex-row items-cente">
      <div className="rounded-full w-8 h-8 flex items-center justify-center shrink-0 relative mr-2">
        <div
          className={cn("tracker-loading-ring-brand rounded-full")}
          style={{
            "--gradient-start": "var(--edgen-content_brand_primary)",
          }}
        />
        <Icon
          name={reportType === "SUMMARY_NEWS" ? "tab-news" : "portfolio_solid"}
          size={20}
          color={ospThemes.colors.content_brand_primary}
        />
      </div>
      <div className={cn("headline_regular_s text-content_brand_primary")}>
        {title}
      </div>
    </div>
    <div className="flex ">
      <Icon
        data-openreplay-label="close"
        name="close"
        size={24}
        color={ospThemes.colors.content_primary}
        onClick={onClose}
      />
    </div>
  </div>
);

export const TrackerReportItem: FC<TrackerReportItemProps> = ({
  type = "list",
  id: _id,
  isExpanded = false,
  created,
  summary,
  toggleExpand,
  onChartClick,
  modalParams,
  reportType = "DAILY_REPORT",
}: TrackerReportItemProps) => {
  const { onClose, isStatusLoading } = modalParams || {};
  const queryClient = useQueryClient();
  const { data: memberInfo } = useMemberUser();
  const [isPC] = useIsPc();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const reportScrollRef = useRef<HTMLDivElement | null>(null);
  const { setIsScrollingUp, setdisabled } = useScrollManager({
    topOffset: 8,
    scrollContainerRef: reportScrollRef,
  });

  const openAutoScroll = useCallback(() => {
    setdisabled(false);
    setIsScrollingUp(false);
  }, [setIsScrollingUp, setdisabled]);
  const {
    getAiSummary,
    isLoading,
    summary: summaryReport,
  } = useSummaryStream();

  const handleOpenChatModal = useOpenChatModal();

  const hasReport = useMemo(() => {
    return !!(summary || summaryReport);
  }, [summary, summaryReport]);

  const title = useMemo(() => {
    if (reportType === "SUMMARY_NEWS") {
      return I18n.t("Market Movers", {
        var0: dayjs(created).format("MMM DD YYYY"),
      });
    }
    // 有报告
    if (hasReport) {
      if (memberInfo?.isFree) {
        return isPC
          ? I18n.t("Macro Daily Report %{var0}", {
              var0: dayjs(created).format("MMM DD YYYY"),
            })
          : I18n.t("Macro Daily Report");
      }
      if (memberInfo?.isPro || memberInfo?.isExpert) {
        return isPC
          ? I18n.t("Portfolio Tracker %{var0}", {
              var0: dayjs(created).format("MMM DD YYYY"),
            })
          : I18n.t("Portfolio Tracker %{var0}", {
              var0: "",
            });
      }
    }
    // 没有报告
    if (memberInfo?.isFree) {
      return I18n.t("Macro Daily Report");
    }
    if (memberInfo?.isPro || memberInfo?.isExpert) {
      return I18n.t("Portfolio Tracker");
    }
  }, [
    hasReport,
    memberInfo?.isFree,
    memberInfo?.isPro,
    memberInfo?.isExpert,
    isPC,
    created,
    reportType,
  ]);

  const generateReport = useMemoizedFn(async () => {
    openAutoScroll();
    const result = await getAiSummary({
      agentId: reportType === "DAILY_REPORT" ? "0" : "",
      type: reportType,
      upgradeCallback: () => {
        setIsUpgradeModalOpen(true);
        onClose?.();
      },
    });
    if (result) {
      // 清除 daily report 和 report list 的缓存
      setTimeout(() => {
        localStorage.setItem("is_daily_report_loaded", "true");
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.AGENT_DAILY_REPORT],
          }),
        ]);
      }, 2000);
    }
  });

  const handleLinkClick = useCallback(
    async (href: string, _event: React.MouseEvent<HTMLAnchorElement>) => {
      log.debug("[TrackerReportItem] Link clicked:", href);

      try {
        const url = new URL(href);
        const key = url.searchParams.get("key") || "";
        const question = url.searchParams.get("question");

        const [val = "", ...signRest] = key.split("_") || [];
        const sign = question ? key : signRest?.join("_");

        if (val || question) {
          await EdgenDrawerCallable.end(false);
          await EdgenModalCreateAble.end(false);
          setTimeout(async () => {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
            log.debug("[TrackerReportItem] Link key:", val, question);
            handleOpenChatModal({
              question: question ? question : val,
              sign,
            });
          }, 500);
        } else {
          // 如果没有 key 参数，使用默认跳转
          window.location.href = href;
        }
      } catch (error) {
        log.error("[TrackerReportItem] Error handling link click:", error);
        // 出错时使用默认跳转
        window.location.href = href;
      }
    },
    [handleOpenChatModal],
  );

  useEffect(() => {
    log.debug("[TrackerReportItem] type", hasReport);
    if (
      type === "modal" &&
      !hasReport &&
      !isLoading &&
      !isStatusLoading &&
      !isUpgradeModalOpen
    ) {
      generateReport();
    }
  }, [
    type,
    toggleExpand,
    hasReport,
    generateReport,
    isLoading,
    isStatusLoading,
  ]);

  return (
    <div
      data-openreplay-label="TimeLineItem · TrackerReportItem · div"
      className={cn("rounded-3 bg-background_solid_tertiary cursor-pointer", {
        "p-3 md:p-4": hasReport && type === "list",
        "p-3 gradient-border-brand-2":
          !hasReport && !isLoading && type === "list",
        "bg-background_solid_tertiary": type === "list",
        "bg-background_solid_secondary h-full w-full overflow-hidden flex flex-col":
          type === "modal",
      })}
      onClick={(e) => {
        if (type === "modal") {
          return;
        }
        e.stopPropagation();
        // 点击整个卡片区域，只能展开，不能收起
        if (!isExpanded) {
          toggleExpand?.(true);
        }
      }}
    >
      {isLoading && type === "list" && (
        <TrackerReportItemLoading
          text={I18n.t("Putting together your report and possible next moves")}
        />
      )}

      {isLoading && type === "modal" && (
        <div className="w-full flex">
          <H5Loading title={title} onClose={onClose} reportType={reportType} />
        </div>
      )}

      {!isLoading && (
        <div
          className={cn("flex justify-between", {
            "md:flex-row flex-col items-start md:items-center ":
              !hasReport && type === "list",
            "flex-row items-center": hasReport || type === "modal",
          })}
        >
          <div className="flex items-stretch gap-4">
            {!hasReport && type === "list" && (
              <div className="flex flex-col w-12 shrink-0 bg-background_brand_overlay20 rounded-2 items-center justify-center min-h-[58px]">
                <div className="headline_regular_s text-content_primary">
                  {dayjs(created).format("DD")}
                </div>
                <div className="body_medium_xs text-content_primary">
                  {dayjs(created).format("MMM")}
                </div>
              </div>
            )}
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-1">
                <div
                  className={cn("flex items-center justify-center shrink-0", {
                    "w-[14px] h-[14px]": !hasReport && type === "list",
                    "w-[22px] h-[22px] rounded-full bg-background_brand_overlay10":
                      hasReport && type === "list",
                    "w-[20px] h-[20px]": type === "modal",
                  })}
                >
                  <Icon
                    name={
                      reportType === "SUMMARY_NEWS"
                        ? "tab-news"
                        : "portfolio_solid"
                    }
                    size={type === "list" ? 14 : 20}
                    color={ospThemes.colors.content_brand_primary}
                  />
                </div>
                <div
                  className={cn("text-content_primary", {
                    headline_regular_s: type === "modal",
                    body_bold_s: type === "list",
                  })}
                >
                  {title}
                </div>
                {hasReport && (
                  <div className="body_s text-content_tertiary ml-[6px]">
                    {dayjs(created).format("HH:mm")}
                  </div>
                )}
              </div>
              {!hasReport && type === "list" && (
                <div className="title_l text-content_primary mt-0.5">
                  {I18n.t("See what Edggy found for your positions today.")}
                </div>
              )}
              {memberInfo?.isFree && !hasReport && type === "list" && (
                <div className="body_s text-content_secondary">
                  {/* <ShellLink
                    title="Tracker Report Item"
                    aria-label="Tracker Report Item"
                    data-openreplay-label={`Tracker Report Item Link | /member`}
                    id="lnk-tracker-report-item"
                    href="/member"
                  >
                    <span className="text-content_brand_primary underline mr-1">
                      {I18n.t("Upgrade to Pro")}
                    </span>
                  </ShellLink> */}
                  {I18n.t("for asset-specific reports.")}
                </div>
              )}
            </div>
          </div>
          {type === "modal" && (
            <div className="flex ">
              <Icon
                data-openreplay-label="close"
                name="close"
                size={24}
                color={ospThemes.colors.content_primary}
                onClick={onClose}
              />
            </div>
          )}

          {!isLoading && !hasReport && type === "list" && (
            <button
              aria-label="Tracker Report Item"
              data-openreplay-label="Tracker Report Item Button"
              id="btn-tracker-report-item"
              className="button sm_button primary_button md:w-20 w-full md:mt-0 mt-4"
              onClick={() => {
                toggleExpand?.(true);
                generateReport();
              }}
            >
              <span className="button_m text-content_inverse_primary">
                {I18n.t("Generate")}
              </span>
            </button>
          )}
          {!isLoading && hasReport && type === "list" && (
            <div className="flex items-center">
              {summary && (
                <button
                  aria-label="Tracker Report Item"
                  data-openreplay-label="Tracker Report Item Button"
                  id="btn-tracker-report-item-2"
                  className="button xs_button button_icon text-content_primary flex items-center gap-1"
                  onClick={(e) => {
                    log.debug("[文案]onChatClick", _id);
                    e.stopPropagation();
                    e.preventDefault();
                    onChartClick?.({
                      type: "SUMMARY",
                      link: "",
                      title: I18n.t("Portfolio Tracker %{var0}", {
                        var0: dayjs(created).format("MMM DD YYYY"),
                      }),
                      id: _id,
                      question: I18n.t("Give me more info about this report"),
                      desc: summary,
                    });
                  }}
                >
                  <Icon
                    name="p_chat"
                    size={16}
                    color={ospThemes.colors.content_primary}
                  />
                  <span className="button_s">{I18n.t("Ask")}</span>
                </button>
              )}
              <span className="border-r border-border_solid h-4 pl-1 mr-1" />
              <Icon
                data-openreplay-label="chevron-down"
                name={"chevron-down"}
                size={20}
                color={ospThemes.colors.content_primary}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleExpand?.(!isExpanded);
                }}
                className={cn(
                  "transition-transform duration-200 cursor-pointer button_icon px-[2px]",
                  isExpanded && "rotate-180",
                )}
              />
            </div>
          )}
        </div>
      )}
      {(isStatusLoading || isLoading) && type === "modal" && (
        <div className="w-full h-full mt-4">
          <LoadingSkeleton />
        </div>
      )}
      <div
        className={cn("hidden", {
          "flex flex-col": hasReport,
          "overflow-y-hidden": type === "modal",
        })}
      >
        {summary && (
          <div
            className={cn(
              "transition-all duration-200",
              type === "list" && "h-[156px] overflow-hidden",
              type === "list" && isExpanded && "h-fit overflow-auto",
              { "overflow-y-auto no-scrollbar": type === "modal" },
            )}
          >
            <Markdown
              className="[&_a]:!body_output [&_div]:!body_output_m [&_h1]:!mb-0 [&_h2]:!mb-0 [&_h3]:!mb-0 [&_p]:!mt-3 [&_p]:!mb-0 [&_p]:!body_output_m [&_li]:!body_output_m [&_span]:!body_medium_m [&_li]:!pt-2 [&_li]:!pb-0 [&_td_div]:!line-height-[20px]"
              linkTarget="_self"
              onLinkClick={handleLinkClick}
            >
              {summary || ""}
            </Markdown>
          </div>
        )}
        <div className="relative">
          <div
            className={cn(
              "body_m text-content_secondary overflow-y-auto no-scrollbar",
              isExpanded && type === "list" && "max-h-[380px]",
            )}
            ref={reportScrollRef}
          >
            {((summaryReport && type === "list") ||
              (type === "modal" && summaryReport && !summary)) && (
              <div
                className={cn(
                  type === "list" ? "h-[156px]" : "h-full",
                  type === "list" && isExpanded && "h-[380px]",
                )}
              >
                <Markdown
                  linkTarget="_self"
                  className="[&_a]:!body_output [&_div]:!body_output_m [&_h1]:!mb-0 [&_h2]:!mb-0 [&_h3]:!mb-0 [&_h4]:!mb-0 [&_p]:!mt-3 [&_p]:!mb-0 [&_p]:!body_output_m [&_li]:!body_output_m [&_span]:!body_medium_m [&_li]:!pt-2 [&_li]:!pb-0 [&_td_div]:!line-height-[20px]"
                  onLinkClick={handleLinkClick}
                >
                  {summaryReport}
                </Markdown>
              </div>
            )}
          </div>
          {!isExpanded && !isLoading && (
            <div
              className="absolute md:bottom-[-16px] bottom-[-12px] left-0 w-full h-[58px] pointer-events-none"
              style={{
                background:
                  "linear-gradient(0deg, var(--edgen-background_solid_tertiary) 0%, rgba(37, 37, 37, 0.00) 77.59%)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
